import * as Sentry from '@sentry/vue';

import {
  AddressBookData,
  AddressBookDefaultData,
  AddressBookEndpointParam,
  AddressBookEndpointResponse,
  AddressBookLookupData,
  AddressBookRequestData,
} from '@/models/addressBook';
import {
  AirRoadBookingRequest,
  AirRoadDataParams,
  AirRoadGeneralJob,
  AirRoadGeneralListPayload,
  AirRoadGeneralPayload,
  AirRoadJobsRequestData,
  AirRoadJobsResponseData,
  AirRoadPrintParams,
  AirRoadResponse,
  AirRoadUpdateParams,
  AirroadJobDetailed,
} from '@/models/airRoad';
import {
  AmPmJobs,
  DriverRetransmit,
  ReportRequestParams,
  ReportResponse,
  OutgoingMachine,
  ReportWeeklyStats,
  ReturnLabelsPrintParams,
  RunSheetDriverDay,
  TomorrowReportData,
  TomorrowTobeSchedReportData,
  TransfersReportData,
  UninvoicedJobsResponse,
} from '@/models/reports';
import {
  AxiosStatusResponse,
  BookingResults,
  DetailedJobSendMessageResponce,
  Division,
  FileCustom,
  IDetailedJobSendMessage,
  IGetJobs,
  ILookUp,
  JobArticleData,
  JobAttachFiles,
  JobBookingJobData,
  JobsBookedApiData,
  JobsBookedResponse,
  LookupDriverFull,
  LookupPostcodeData,
  MessageVisibilityParams,
  ModelLookupChosenValue,
  ModuleNotifications,
  PostcodeDataResponce,
  ProductLocation,
  ReferencesParams,
  SpreadheetParams,
  StopCompleteParams,
  SuburbGNAFLookUpData,
  User,
  UserPrintersData,
} from '@/models/main';
import { DraftsRequestData, DraftsResponseData, JobDraftParams, JobDraftsListItem } from '@/models/draft';
import { DriverLocations, DriverLocationsParams, NextDayStopsParams, Package } from './models/location';
import { GeneralBookingFormData, GeneralBookingProductData } from '@/models/generalBooking';
import { JobCancelParams, JobFutileParams, JobHistory } from '@/models/jobDetailed';
import {
  JobChangeDateParams,
  SchedulerAllocationsResponse,
  SchedulerBoardCreated,
  SchedulerBoardLogsData,
  SchedulerBoardsPayload,
  SchedulerSaveBoardResponse,
  SchedulerScheduledStopsList,
  SchedulerShift,
  SchedulerShiftItem,
  SchedulerTravelTimeForBoardResponse,
  SchedulerTravelTimeResponse,
  SchedulerUnscheduledStop,
} from '@/models/scheduler';
import {
  JobComplianceAttribute,
  JobComplianceData,
  JobComplianceDriver,
  JobComplianceDriverAllocateParams,
  JobComplianceHoldResponse,
  JobComplianceProductLookupData,
  JobComplianceServiceLookupData,
  JobComplianceStop,
  JobComplianceStopProductData,
  JobComplianceStopService,
  JobEditCancelParams,
  JobEditChangeCustomerParams,
  JobLockResponse,
} from '@/models/JobComplianceTypes';
import { JobTicket, JobTicketAtricle, JobTicketDashboardResponse, JobTicketResponce } from '@/models/jobTicket';
import { QuantListData, TQuantDataParams } from '@/models/jobQuants';
import { TechnicianCatalogItem, TechniciansJobsResponseData } from './models/technicians';
import { abortController, getControllerSignal } from '@/utils/abortController';
import axios, { AxiosInstance, AxiosPromise, AxiosRequestConfig, AxiosResponse } from 'axios';

import { ISelectSuburb } from '@/models/jbAdvanced';
import { JobSchema } from './models/jobData';
import {
  PermanentBookingResponse,
  PermanentCreatePayload,
  PermanentListResponse,
  PermanentTemplate,
} from '@/models/permanentJob';
import {
  QuoteCalculateRequest,
  QuoteCalculateResponse,
  QuoteDetail,
  QuoteFile,
  QuoteListItem,
  QuoteListResponse,
  QuoteOptions,
  QuoteSaveRequest,
  QuoteUpdateRequest,
} from '@/models/quote';
import { ModuleNotificationsItemsData } from '@/store/modules/notifications/notificationsTypes';
import { components } from '@/models/jobDetailed';
import jwtDecode from 'jwt-decode';
import { sendError } from '@/helpers/sentry';
import store from '@/store';

type JobDetailedData = components['schemas']['Job'];

declare module 'axios' {
  export interface AxiosRequestConfig {
    skipAuth?: boolean;
  }
}

const api: AxiosInstance = axios.create({
  baseURL: process.env.VUE_APP_API,
  // validateStatus: function (status) {
  //   return status < 500; // Resolve only if the status code is less than 500
  // },
});

/**
 *
 * ** Request interceptor for API calls **
 *
 */
api.interceptors.request.use(
  async config => {
    // Skip for logIn and refreshAccessToken requests
    if (config.skipAuth) {
      return config;
    }
    // you can pass signal to config to handle aborting requests by yourself
    if (!config.signal) {
      config.signal = getControllerSignal();
    }
    const token = await getValidAccessToken();
    config.headers = config.headers ?? {};
    config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  async error => await Promise.reject(error),
);
/**
 * Response interceptor
 */
api.interceptors.response.use(
  response => response,
  error => {
    if (error.response?.status === 401) {
      Sentry.addBreadcrumb({
        type: 'debug',
        message: `The session has expired. ${error.response?.config.url}`,
        category: 'debug',
      });
      abortController();
      void store.dispatch('moduleAuthData/forceRenewRefreshToken');
    } else {
      if (!axios.isCancel(error)) {
        sendError(error, 'api.interceptors.response');
      }
    }
    return Promise.reject(error);
  },
);
/**
 *
 * // Request interceptor for API calls //
 *
 */

/**
 *
 * ** Token endpoints: **
 *
 */

/**
 * A variable to hold refresh token request to reduce race conditions
 */
let refreshTokenRequests: Promise<string> | null = null;

/**
 * Get valid AccessToken for the axios request interceptor
 */
async function getValidAccessToken(): Promise<string> {
  // get access token from the store
  let access_token = store.getters['moduleAuthData/authData'].access;
  const now = Math.floor(Date.now() * 0.001);

  // If RefreshToken is not valid => LogOut procedure
  if (!store.getters['moduleAuthData/isRefreshTokenValid'](now)) {
    // First of all, we need to abort all requests
    abortController();
    // LogOut procedure
    await store.dispatch('moduleAuthData/forceRenewRefreshToken');
  } else if (!store.getters['moduleAuthData/isAccessTokenValid'](now)) {
    // check if we already have any refresh requests
    // create request and save it into variable
    if (refreshTokenRequests === null) {
      refreshTokenRequests = refreshAccessToken();
    }
    // resolve the request
    access_token = await refreshTokenRequests;
    // erase the variable
    refreshTokenRequests = null;
  }
  return access_token;
}

/**
 POST – Authentificates user and gives tokens
 * Initial login to get user's JWT
 * @param data
 * @constructor
 */
export const logIn = async (data: { username: string; password: string }): Promise<boolean> => {
  const response = await api.post('/token/', data, { skipAuth: true });
  if (response.status === 200) {
    const { permissions, exp, name, customer_code, available_customer_codes } = jwtDecode(response.data.access) as any;
    const params = {
      refresh: response.data.refresh,
      access: response.data.access,
      permissions,
      exp,
      name,
      customerCode: customer_code,
      availableCustomerCodes: available_customer_codes,
    };
    await store.dispatch('moduleAuthData/addAuthData', params);
    return true;
  }
  return false;
};

/** POST token/refresh/ – Takes a refresh type JSON web token and returns an access
 * type JSON web token if the refresh token is valid.
 * Refresh AccessToken using valid RefreshToken
 */
async function refreshAccessToken(): Promise<string> {
  const response = await api.post(
    '/token/refresh/',
    { refresh: store.getters['moduleAuthData/authData'].refresh },
    { skipAuth: true },
  );
  await store.dispatch('moduleAuthData/updateAccessToken', response.data.access);
  return response.data.access;
}
/**
 *
 * // Token endpoints //
 *
 */

/**
 *
 * ** Common functions: **
 *
 */

// creating the string in '?&dates=2022-01-06,2022-01-13&customer_code=RICOH,CANON&state=NSW,NT,NZ' format
// TODO: type for random object except 'any'?
export function generateQueryString(
  data:
    | []
    | ILookUp
    | AirRoadDataParams
    | AirRoadGeneralListPayload
    | AddressBookEndpointParam
    | AirRoadJobsRequestData
    | ReportRequestParams
    | LookupPostcodeData
    | DriverLocationsParams
    | NextDayStopsParams
    | JobChangeDateParams,
): string {
  const query: string[] = [];
  for (const key in data) {
    // TODO: add switch case
    if (data[key] || typeof data[key] === 'boolean') {
      query.push(`${key}=${encodeURIComponent(data[key])}`);
    }
  }
  return query.length ? '?' + query.join('&') : '';
}
/**
 *
 * // Common functions //
 *
 */

/**
 *
 * ** Common endpoints: **
 *
 */
export const getFileFromUrl = async (link: string): Promise<Blob> => {
  return (await api.get(link, { responseType: 'blob' })).data;
};

export const getVersion = async (): Promise<string> => {
  return await axios({
    url: 'https://' + window.location.hostname + '/version.json',
    method: 'GET',
  }).then(res => res.data.version);
};
/**
 *
 * // Common endpoints //
 *
 */

/**
 *
 * ** Profile: **
 *
 */
// GET /user/profile/
export const getProfile = async (): Promise<User> => {
  return (await api.get('/user/profile/')).data;
};

// PUT /user/profile/ – Updates user profile
// res.status === 200
export const updateProfile = async (params: FormData): Promise<AxiosResponse> => {
  return await api.put('/user/profile/', params, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
};
/**
 *
 * // Profile //
 *
 */

export const getUserPrinters = async (): Promise<UserPrintersData[]> => {
  return (await api.get('/user/printer_options/')).data;
};

/**
 *
 * ** Dashboard and dashboard like tables: **
 *
 */

// POST /dashboard/jobs/ – Returns all jobs related to specified customer code or all jobs at all if user is staff
export const getJobs = async (date_filter: string[]): Promise<IGetJobs> => {
  return (await api.post('dashboard/jobs/', { date_filter })).data;
};

//  path('api/dashboard/bfixjobs/', bfix_dashboard.BFIXDashboardView.as_view(), name='dashboard-bfixjobs'),
export const getBfixJobs = async (date_filter: string[]): Promise<IGetJobs> => {
  return (await api.get(`dashboard/bfixjobs/?date_filter=${date_filter}`)).data;
};

// GET /dashboard/search/ – Looks through ALL jobs in Kraken
export const searchJobs = async (params: ILookUp): Promise<IGetJobs> => {
  const queryString = generateQueryString(params);
  return (await api.get(`dashboard/search/${queryString}`)).data;
};

// GET /dashboard/search/pdf/ Returns PDF export of search results
export const searchJobsPDF = async (params: ILookUp): Promise<AxiosResponse> => {
  const queryString = generateQueryString(params);
  return await api.get(`dashboard/search/pdf/${queryString}`, { responseType: 'blob' });
};

// POST /dashboard/job/note/ – API endpoint for dashboard which creates note object
export const postJobEdit = async (params: Record<string, unknown>, user: string): Promise<void> => {
  const response = await api.post('dashboard/job/note/', { params });
  if (response.status === 200) {
    await store.dispatch('addNote', { created_by: user, ...params });
  }
};

// GET /dashboard/job/detailed/ – Returns data needed for Detailed Job view
export const getDashboardJobDetailed = (
  jobId: number,
  jobDate: string,
  requestConfig?: AxiosRequestConfig,
): Promise<AxiosResponse> => {
  return api.get(`/dashboard/job/detailed/?job_id=${jobId}&job_date=${jobDate}`, requestConfig);
};
export const getDetailedJob = async (jobId: number, jobDate: string): Promise<JobDetailedData> => {
  return (await getDashboardJobDetailed(jobId, jobDate)).data;
};

// GET /dashboard/job/detailed/:id: – Returns data needed for Detailed Job view by RH id only
export const getDashboardJobDetailedJob = (
  jobId: number,
  requestConfig?: AxiosRequestConfig,
): Promise<AxiosResponse> => {
  return api.get(`/dashboard/job/detailed/${jobId}/`, requestConfig);
};
export const getDetailedJobById = async (jobId: number): Promise<JobDetailedData> => {
  return (await getDashboardJobDetailedJob(jobId)).data;
};

// GET /job/airroad/: – Returns AirRoad detailed data for Detailed Job view by RH id and date
export const getDetailedAirRoadJobByRHId = async (id: number, date: string): Promise<[AirroadJobDetailed]> => {
  return (await api.get(`job/airroad/?job_id=${id}&job_date=${date}`)).data;
};

// GET /dashboard/customer/ – Detailed Customer view
export const getDashboardCustomer = (
  customerCode: string,
  requestConfig?: AxiosRequestConfig,
): Promise<AxiosResponse> => {
  return api.get(`/dashboard/customer/?code=${customerCode}`, requestConfig);
};
export const getReferencesParams = async (customerCode: string): Promise<ReferencesParams> => {
  return (await getDashboardCustomer(customerCode)).data;
};

// GET /dashboard/booking/user_log/ – Shows log records of booked by NewBookingProcedure jobs
export const getJobsBookedByUser = async (
  user: string,
  dates: string[],
  page: number,
  pageSize: number,
): Promise<JobsBookedResponse> => {
  const params = `user=${user}&dates=${dates.toString()}&page=${page}&page_size=${pageSize}`;
  return (await api.get(`/dashboard/booking/user_log/?${params}`)).data;
};

// GET /dashboard/booking/customer_log/ – Shows log records of booked by NewBookingProcedure jobs
export const getJobsBookedByCustomer = async (
  customer: string,
  dates: string[],
  page: number,
  pageSize: number,
): Promise<JobsBookedResponse> => {
  const params = `customer=${customer}&dates=${dates.toString()}&page=${page}&page_size=${pageSize}`;
  return (await api.get(`/dashboard/booking/customer_log/?${params}`)).data;
};

// GET /dashboard/booking/log_original_data/ – Shows original JSON data from NBP log
export const getJobsBookedLog = async (id: number): Promise<JobsBookedApiData[]> => {
  return (await api.get(`/dashboard/booking/log_original_data/?log_id=${id}`)).data;
};
/**
 *
 * // Dashboard and dashboard like tables //
 *
 */

/**
 *
 * ** Job related: **
 *
 */

// GET /job/printout/ – Generates PDF and sends back
export const getShippingPDF = async (job_id: number, job_date: string): Promise<Blob> => {
  return (await api.get(`job/printout/?job_id=${job_id}&job_date=${job_date}`, { responseType: 'blob' })).data;
};

export const postJobClone = (params: JobDetailedData[], requestConfig?: AxiosRequestConfig): Promise<AxiosResponse> => {
  return api.post('/job/clone/?dry_run=false', params, requestConfig);
};

// POST /job/can_schedule/ – Sets job's stop's can_schedule status res.status === 201
export const setScheduleStatus = async (
  schedule: boolean,
  id: number,
  date: string,
  requestConfig?: AxiosRequestConfig,
): AxiosPromise<AxiosStatusResponse> => {
  const params = {
    can_schedule: schedule,
    job_id: id,
    job_date: date,
  };
  return await api.put('/job/can_schedule/', params, requestConfig);
};

// POST /job/message/ – Sends message to RH
export const detailedJobSendMessage = async (
  params: IDetailedJobSendMessage,
): Promise<DetailedJobSendMessageResponce> => {
  return (await api.post('job/message/', params)).data;
};

// POST /job/message/visibility/ – Sets job's message status - private or public
// res.status === 201
export const changeJobDetailedMessageVisibility = async (params: MessageVisibilityParams): Promise<AxiosResponse> => {
  return api.post('/job/message/visibility/', params);
};

// GET /job/pod/ – Returns proof of delivery image by job's stop
export const getPOD = async (jobId: number, jobDate: string, stopNumber: number): Promise<Blob | string> => {
  return await api
    .get(`job/pod/?job_id=${jobId}&job_date=${jobDate}&stop_number=${stopNumber}`, {
      responseType: 'blob',
      // We need to override default validateStatus function to allow 404 status code and return string for error path.
      validateStatus: (status: number) => status === 200 || status === 404,
    })
    .then(res => window.URL.createObjectURL(new Blob([res.data])) as unknown as Blob)
    .catch(() => 'Not found');
};

// POST /job/book_xls/ – Books jobs from XLS-template
// can be refactored .data
export const uploadJobsSpreadsheet = async (file: FileCustom, params: SpreadheetParams): Promise<AxiosResponse> => {
  const filename = file.filename;
  const bodyFormData = new FormData();
  bodyFormData.append('file', file);
  return await api.post(`job/book_xls/?filename=${filename}&dry_run=${params.dry_run}`, bodyFormData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
};
// POST /job/book_b2b_xls/ – Books B2B jobs from XLS-file
export const uploadB2BJobsSpreadsheet = async (
  file: FileCustom,
  params: SpreadheetParams,
  serviceCode: string,
): Promise<AxiosResponse> => {
  const filename = file.filename;
  const bodyFormData = new FormData();
  bodyFormData.append('file', file);
  return await api.post(
    `job/book_b2b_xls/?filename=${filename}&dry_run=${params.dry_run}&service_code=${serviceCode}`,
    bodyFormData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    },
  );
};

// POST /job/book_api/ – Books jobs from web API
// res.status === 201
export const bookJobAdvanced = async (params: JobBookingJobData[]): Promise<AxiosResponse> => {
  return await api.post('job/book_api/?dry_run=false', params);
};

export const bookJobWhToWh = async (
  params: JobSchema[],
  requestConfig?: AxiosRequestConfig,
): Promise<AxiosResponse> => {
  return await api.post('job/book_wh2wh/?dry_run=false', params, requestConfig);
};

export const bookJobWhToSWh = async (
  params: JobSchema[],
  requestConfig?: AxiosRequestConfig,
): AxiosPromise<BookingResults> => {
  return await api.post('job/book_wh2swh/?dry_run=false', params, requestConfig);
};

export const bookJobSiteInspection = async (params: JobBookingJobData[]): AxiosPromise<BookingResults> => {
  return await api.post('job/book_si/?dry_run=false', params);
};

export const bookJobBreakFix = async (params: JobSchema[]): AxiosPromise<BookingResults> => {
  return await api.post('job/book_bfix/?dry_run=false', params);
};

// GENERAL BOOKING
// POST /job/book_3pl/ – Books jobs from web API - General booking
export const bookJobGeneral = async (params: GeneralBookingFormData[]): Promise<AxiosResponse> => {
  return await api.post('job/book_3pl/?dry_run=false', params);
};

// Products list:
export const getProductsGeneral = async (): Promise<GeneralBookingProductData[]> => {
  return (await api.get('/job/product/general/')).data;
};

// GET Zammad attachments – Gets attachment by ticket_id, article_id and file_id
export const getJobTicketAttachment = async (
  ticket_id: number,
  article_id: number,
  file_id: number,
): Promise<AxiosResponse> => {
  return api.get(
    `/zammad/article-attachment/?ticket_id=${ticket_id}&article_id=${article_id}&attachment_id=${file_id}`,
    {
      responseType: 'blob',
    },
  );
};

// POST /job/attachment/ – Attaches files to job
/**
 * Attach file(s) to a job using jobId and jobDate
 * @param params
 */
// can be refactored .data
export const uploadJobFiles = async (params: JobAttachFiles): Promise<AxiosResponse> => {
  const bodyFormData = new FormData();
  bodyFormData.append('rh_id', String(params.rh_id));
  bodyFormData.append('rh_date', params.rh_date);
  params.files.map(file => {
    bodyFormData.append('file', file);
  });
  return await api.post('job/attachment/', bodyFormData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
};

/**
 * DELETE /job/attachment/{id}/ – Deletes attached file
 * Delete file attachment from a Job by attachment's ID
 * @param id
 */
export const deleteJobAttachment = async (id: number): Promise<AxiosResponse> => {
  return (await api.delete(`job/attachment/${id}/`)).data;
};

/**
 * Delete image from a Job stop by image path
 */
export const deleteJobImage = async (jobId: number, jobDate: string, imagePath: string): Promise<AxiosResponse> => {
  return await api.delete(
    `job/stop/image/?job_id=${jobId}&job_date=${jobDate}&image_path=${encodeURIComponent(imagePath)}`,
  );
};

// GET /job/resync/ – Re-syncs a single job from the RH
// response is not using
export const getJobReSync = async (job_id: number, job_date: string): Promise<AxiosResponse> => {
  return await api.get(`job/resync/?job_id=${job_id}&job_date=${job_date}`);
};

// res.status === 200
export const syncJobs = async (): Promise<AxiosResponse> => {
  return await api.get('job/daysync/');
};
/**
 *
 * // Job related //
 *
 */

/**
 *
 * ** Common lookups: **
 *
 */

// GET /lookup/customer/ – Lookup for customers
export const lookupCustomer = async (params: ILookUp): Promise<string[]> => {
  const queryString = generateQueryString(params);
  return (await api.get(`lookup/customer/${queryString}`)).data;
};

// GET /lookup/suburb/ – Look up Suburb which name starts with suburb_guess
export const getLookupSuburb = (val: string, requestConfig?: AxiosRequestConfig): Promise<AxiosResponse> => {
  return api.get(`/lookup/suburb/?suburb_guess=${encodeURI(val)}`, requestConfig);
};
export const getSuburb = async (val: string): Promise<ISelectSuburb[]> => {
  return (await getLookupSuburb(val)).data;
};

// GET /lookup/service – Lookup for Service codes
export const lookupService = async (params: ILookUp): Promise<string[]> => {
  const queryString = generateQueryString(params);
  return (await api.get(`lookup/service/${queryString}`)).data;
};

// GET /lookup/driver_by_job/ – Lookup for drivers
export const lookupDriver = async (params: ILookUp): Promise<string[]> => {
  const queryString = generateQueryString(params);
  return (await api.get(`lookup/driver_by_job/${queryString}`)).data;
};

// GET /lookup/driver/ – (+) manually edited
export const getDriversLookup = async (driver: string): Promise<LookupDriverFull[]> => {
  return (await api.get(`/lookup/driver/?driver=${driver}`)).data;
};

// GET /lookup/product/model/ – Lookup for product model with product_id and model/alias name
export const getLookupProductModel = (val: string, requestConfig?: AxiosRequestConfig): Promise<AxiosResponse> => {
  return api.get(`/lookup/product/model/?model=${encodeURIComponent(val)}`, requestConfig);
};
export const getProductModel = async (model: string): Promise<[]> => {
  return (await getLookupProductModel(model)).data;
};

export const getFinisherByName = async (model: string, customer: string): Promise<ModelLookupChosenValue[]> => {
  return (await api.get(`lookup/product/finisher/?model=${model}&customer=${customer}`)).data;
};

// GET /lookup/product/location/ – Lookup for product location in ODOO
export const getProductLocation = async (product_id: number, serial: string): Promise<ProductLocation> => {
  return (await api.get(`lookup/product/location/?product_id=${product_id}&serial=${encodeURIComponent(serial)}`)).data;
};

// GET /lookup/address/ – Lookup for addresses of client
// TODO: Refactor used of this function to use getAddressLookup which return full response, not data
export const getAddressLookupData = async (customer: string): Promise<AddressBookLookupData[]> => {
  return (await api.get(`lookup/address/?customer=${customer}`)).data;
};

export const getAddressLookup = async (customer: string): AxiosPromise<AddressBookLookupData[]> => {
  return await api.get(`lookup/address/?customer=${customer}`);
};

// GET /lookup/suburb-gnaf/ – Lookup for suburb
export const getLookupGNAFSuburb = async (suburb_guess: string, postcode: string): Promise<SuburbGNAFLookUpData[]> => {
  return (await api.get(`lookup/suburb-gnaf/?suburb_guess=${suburb_guess}&postcode=${postcode}`)).data;
};

// Get list of divisions
export const getDivisions = async (): Promise<Division[]> => {
  return (await api.get('lookup/division/')).data;
};

/**
 *
 * // Common lookups //
 *
 */

/**
 *
 * ** Driver application emulation: **
 *
 */

// POST /driver/job/stop/arrive – Sends signal to RH about driver arrived at Stop
// POST /driver/job/stop/depart – Sends signal to RH about driver departed Stop
export const completeStopFromDashboard = async (
  action: string,
  params: StopCompleteParams,
): Promise<AxiosResponse | null> => {
  // ??? date?
  if (action === 'arrived') {
    return await api.post('driver/stop/arrive/', params);
  }
  if (action === 'departed') {
    return await api.post('driver/stop/depart/', params);
  }
  return null;
};
/**
 *
 * // Driver application emulation //
 *
 */

/**
 *
 * ** Reports: **
 *
 */

// POST /report/bulksignatures/ – Generates ZIP archive with POD signatures
// file
export const postBulkSignature = async (params: Record<'refs', string[]>): Promise<AxiosResponse> => {
  return await api.post('report/bulksignatures/', params, { responseType: 'blob' });
};

// GET /report/runsheet/params/ – Returns dict with list of drivers on date, for user to choose
export const getRunSheetList = async (params: { states: string[]; dates: string[] }): Promise<AxiosResponse> => {
  return (await api.get(`report/runsheet/params/?states=${params.states}&dates=${params.dates}`)).data;
};

// POST /report/runsheet/ – Returns RunSheet report in PDF format
// file
export const getRunSheetPDFs = async (params: [string, number][]): Promise<AxiosResponse> => {
  return await api.post('report/runsheet/', params, { responseType: 'blob' });
};

// POST /report/return-labels/ – Returns KKTS Return Sticker labels in PDF format
// file
export const getReturnLabelsPDF = async (drivers: RunSheetDriverDay[]): Promise<AxiosResponse> => {
  return await api.post('report/return-labels/', { drivers }, { responseType: 'blob' });
};

// POST /report/return-labels/print/ – Sends KKTS Return Sticker labels straight to a label printer
export const printReturnLabels = async (params: ReturnLabelsPrintParams): Promise<AxiosResponse> => {
  return await api.post('report/return-labels/print/', params);
};

// GET /report/eta/ – Generate and returns ETA report in JSON/CSV/PDF formats
// List
// TODO: Very dirty hack to request first 1k records from backend
//  to avoid using pagination in the Vue component. Use it in Vue2 only while we are migrating to Vue3
export const getETAList = async (dates: string[], customerCode: string): Promise<AxiosResponse> => {
  return await api.get(`report/eta/?customer=${customerCode}&date=${dates}&page_size=1000`);
};
// file downloading
export const getETAFile = async (dates: string[], customerCode: string, type = 'JSON'): Promise<AxiosResponse> => {
  return await api.get(`report/eta/?customer=${customerCode}&date=${dates}&type=${type}`, { responseType: 'blob' });
};

// POST /report/eta/ – Generates and sends e-mail with ETA report in CSV format to list of customer's e-mails
// res.status === 200
export const sendETAReport = async (dates: string[], customerCode: string): Promise<AxiosResponse> => {
  return await api.post(`report/eta/?customer=${customerCode}&date=${dates}`);
};

// A general endpoint to get different Report types
export const getReportList = async (params: ReportRequestParams): Promise<ReportResponse[]> => {
  const queryString = generateQueryString(params);
  return (await api.get(`reports/${queryString}`)).data;
};
export const generateReport = async (params: ReportRequestParams): Promise<AxiosResponse> => {
  const queryString = generateQueryString(params);
  return (await api.post(`reports/generate_report/${queryString}`)).data;
};
export const getReportFile = async (report_id: number): Promise<AxiosResponse> => {
  return await api.get(`reports/${report_id}/download/`, { responseType: 'blob' });
};
export const getTomorrowReportFile = async (dates: string[], division: string): Promise<AxiosResponse> => {
  return await api.get(`report/tomorrow/?report_date=${dates}&division=${division}`, { responseType: 'blob' });
};
export const getTomorrowReportShedData = async (dates: string[], division: string): Promise<TomorrowReportData[]> => {
  return (await api.get(`report/tomorrow-sched/?report_date=${dates}&division=${division}`)).data;
};
export const getTomorrowReportSchedPDF = async (dates: string[], division: string): Promise<AxiosResponse> => {
  return await api.get(`report/tomorrow-sched-pdf/?report_date=${dates}&division=${division}`, {
    responseType: 'blob',
  });
};
export const getTomorrowReportTobeSchedData = async (
  dates: string[],
  division: string,
): Promise<TomorrowTobeSchedReportData[]> => {
  return (await api.get(`report/tomorrow-tobesched/?report_date=${dates}&division=${division}`)).data;
};
export const getTomorrowReportTobeSchedPDF = async (dates: string[], division: string): Promise<AxiosResponse> => {
  return await api.get(`report/tomorrow-tobesched-pdf/?report_date=${dates}&division=${division}`, {
    responseType: 'blob',
  });
};
export const getTomorrowReportSplitData = async (dates: string[], division: string): Promise<TomorrowReportData[]> => {
  return (await api.get(`report/tomorrow-split/?report_date=${dates}&division=${division}`)).data;
};
export const getWeeklyStats = async (params: { states: string[]; dates: string[] }): Promise<ReportWeeklyStats> => {
  return (await api.get(`report/weekly_stat/?states=${params.states}&dates=${params.dates}`)).data;
};
export const getAMPMJobs = async (params: { dates: string[] }): Promise<AmPmJobs> => {
  return (await api.get(`report/am_pm_jobs/?date=${params.dates}`)).data;
};
export const getTransfersReport = async (params: {
  dates: string[];
  barcode: string;
}): Promise<TransfersReportData> => {
  return (await api.get(`report/odoo-transfers/?dates=${params.dates}&barcode=${params.barcode}`)).data;
};

// GET /uninvoiced-jobs/ – Uninvoiced/dummy jobs report (staff only)
export interface UninvoicedJobsParams {
  status: 'uninvoiced' | 'dummy';
  customer?: string;
  payment_status?: string;
  from_date?: string;
  to_date?: string;
  page?: number;
  page_size?: number;
}
export const getUninvoicedJobs = async (params: UninvoicedJobsParams): Promise<UninvoicedJobsResponse> => {
  const queryString = generateQueryString(params as any);
  return (await api.get(`uninvoiced-jobs/${queryString}`)).data;
};
export const getUninvoicedJobsCsv = async (params: UninvoicedJobsParams): Promise<AxiosResponse> => {
  const csvParams = { ...params, export: 'csv' };
  const queryString = generateQueryString(csvParams as any);
  return await api.get(`uninvoiced-jobs/${queryString}`, { responseType: 'blob' });
};

// GET /portal/reports/outgoing-machines/ – Outgoing machines report (staff: customer_code required)
export interface OutgoingMachinesParams {
  customer_code: string;
  date_from: string;
  date_to: string;
  // Which date date_from/date_to filter on. Defaults to the job's RH date.
  date_type: 'rh_job_date' | 'delivery_date' | 'completion_date';
  serial_number?: string; // text search on the serial number
  product_type?: 'delivery' | 'pickup'; // omit = both
  completed_only?: boolean; // send true only; omit otherwise
  has_serial?: boolean; // only rows with a serial number; send true only, omit otherwise
}
export const getOutgoingMachines = async (params: OutgoingMachinesParams): Promise<OutgoingMachine[]> => {
  const queryString = generateQueryString(params as any);
  return (await api.get(`portal/reports/outgoing-machines/${queryString}`)).data;
};
export const getOutgoingMachinesCsv = async (params: OutgoingMachinesParams): Promise<AxiosResponse> => {
  const csvParams = { ...params, format: 'csv' };
  const queryString = generateQueryString(csvParams as any);
  return await api.get(`portal/reports/outgoing-machines/${queryString}`, { responseType: 'blob' });
};

/**
 *
 * // Reports //
 *
 */

/**
 *
 * **AirRoad: **
 *
 */

// GET /airroad/
export const getAirRoad = async (params: AirRoadDataParams): Promise<AirRoadResponse> => {
  const queryString = generateQueryString(params);
  return (await api.get(`airroad/jobs/${queryString}`)).data;
};

export const getAirRoadJobsToBook = async (params: AirRoadJobsRequestData): Promise<AirRoadJobsResponseData> => {
  const queryString = generateQueryString(params);
  return (await api.get(`airroad/booking/${queryString}`)).data;
};

// POST /airroad/download_labels/ Returns PDF with labels of jobs from provided list
// file
export const downloadAirRoadJobsPDF = async (ids: number[]): Promise<AxiosResponse> => {
  return await api.post('airroad/jobs/download_labels/', ids, { responseType: 'blob' });
};

// GET /airroad/lookup_locations/ Returns locations of Airroad
export const getAirRoadLocations = async (): Promise<[]> => {
  return (await api.get('airroad/lookup_locations/')).data;
};

// POST /airroad/populate_with_data/ Populates job with a data from a product related to stop
// res.status === 200
export const populateAirRoadJobs = async (ids: number[]): Promise<AxiosResponse> => {
  const formattedParams = {
    job_ids: ids,
  };
  return await api.post('/airroad/populate_with_data/', formattedParams);
};

// POST /airroad/send_to_ar/ Queues jobs from provided list to be sent to AirRoad
// response is not using
export const sendJobToAirRoad = async (ids: number[]): Promise<AxiosResponse> => {
  const formattedParams = {
    job_ids: ids,
  };
  return await api.post('/airroad/send_to_ar/', formattedParams);
};

export const bookAirRoadJob = async (
  params: AirRoadBookingRequest,
  withAllocation?: boolean,
): Promise<AxiosResponse> => {
  return await api.post(`/airroad/booking/send/?with_alloc=${withAllocation ? true : false}`, params);
};

// POST /airroad/revert_to_not_booked/ Returns back to 'Not booked' status job that was booked with error previously
// res.status === 200
export const revertAirRoadJobToNotBooked = async (ids: number[]): Promise<AxiosResponse> => {
  const formattedParams = {
    job_ids: ids,
  };
  return await api.post('/airroad/revert_to_not_booked/', formattedParams);
};

// PUT /airroad/{id}/
// not using resp at all
export const updateAirRoadJobData = async (id: number, params: AirRoadUpdateParams): Promise<AxiosResponse> => {
  return await api.put(`/airroad/${id}/`, params);
};

// GET /airroads/3rdparty/ Lists all jobs related to interstate transportation for further booking AR services
export const getAirRoad3rdparty = async (
  date: string,
  hasAirRoadJob?: boolean,
  id?: number,
): Promise<AirRoadGeneralJob[]> => {
  const params: AirRoadGeneralListPayload = {
    rh_date: date,
    has_ar_job: hasAirRoadJob || false,
    rh_id: id,
  };
  const queryString = generateQueryString(params);
  return (await api.get(`/airroads/3rdparty/${queryString}`)).data;
};

// POST /airroads/3rdparty/ Makes AR booking for provided stops
// res.status === 201
export const sendAirRoad3rdparty = async (params: AirRoadGeneralPayload[]): Promise<AxiosResponse> => {
  return await api.post('/airroads/3rdparty/', params);
};

// PUT /airroads/3rdparty/{id}/ Update AR-related fields in any job
// () – not using data or response
export const updateAirRoad3rdpartyJobData = async (id: number, params: AirRoadUpdateParams): Promise<AxiosResponse> => {
  return await api.put(`/airroads/3rdparty/${id}/`, params);
};

export const printAirRoad = async (params: AirRoadPrintParams): Promise<AxiosResponse> => {
  return await api.post(`/airroad/jobs/${params.id}/print_label/`, { printer_id: params.printer_id });
};
/**
 *
 * // AirRoad //
 *
 */

/**
 *
 * ** Address book: **
 *
 */

// GET  /address_book/addressbooks/ – Returns all
// address books with addresses available to user from Customer and User AddressBook
export const getAddressBooks = async (params: AddressBookEndpointParam): Promise<AddressBookEndpointResponse[]> => {
  const queryString = generateQueryString(params);
  return (await api.get(`address_book/addressbooks/${queryString}`)).data;
};

// GET /address_book/addresses/ –  Returns all addresses available to user + warehouse addresses
export const getAddressBookAddresses = async (
  params: AddressBookEndpointParam,
  requestConfig?: AxiosRequestConfig,
): Promise<AxiosResponse> => {
  const queryString = generateQueryString(params);
  return api.get(`/address_book/addresses/${queryString}`, requestConfig);
};

// POST /address_book/addresses/ – Creates address in addressbook
// res.status === 201
export const createAddressBookItem = async (params: AddressBookData): Promise<AxiosResponse> => {
  return await api.post('address_book/addresses/', params);
};

// PUT /address_book/addresses/{id}/ – Updates address data
// res.status === 200
export const editAddressBookItem = async (
  params: AddressBookData,
  username: string,
  customerCode: string,
): Promise<AxiosResponse> => {
  return await api.put(`address_book/addresses/${params.id}/?username=${username}&customer=${customerCode}`, params);
};

// DELETE /address_book/addresses/{id}/ – Deletes address
// res.status === 204
export const removeAddressBookItem = async (
  id: number,
  username: string,
  customerCode: string,
): Promise<AxiosResponse> => {
  return await api.delete(`address_book/addresses/${id}/?username=${username}&customer=${customerCode}`);
};

// PUT /address_book/addresses/{id}/move_to/ – Moves address to provided position in ordered addressbook
// res.status === 204 / 200
export const moveAddressBookItem = async (id: number, position: number): Promise<AxiosResponse> => {
  return await api.put(`address_book/addresses/${id}/move_to/?position=${position}`);
};

// POST /address_book/user_addressbook/ – Creates address in addressbook
// Used to work under a user. An entry will be creted under the login user
// res.status === 201
export const createUserAddressBookItem = async (params: AddressBookRequestData): Promise<AxiosResponse> => {
  return await api.post('address_book/user_addressbook/', params);
};

// Default address book
export const getAddressBookDefault = async (customer_code: string): Promise<AddressBookDefaultData> => {
  return (await api.get(`address_book/customer/?customer_code=${customer_code}`)).data;
};

export const putAddressBookDefault = async (address_id: number, customer_code: string): Promise<AxiosResponse> => {
  const params = {
    customer_code: customer_code,
  };
  return await api.put(`address_book/customer/?address_id=${address_id}`, params);
};

/**
 *
 * // Address book //
 *
 */

/**
 *
 * ** Scheduler: **
 *
 */

// GET /sched/shift/ – (+) manually edited
export const getSchedulerShifts = async (date: string): Promise<SchedulerShiftItem[]> => {
  return (await api.get(`/sched/shift/?shift_date=${date}`)).data;
};

// GET /sched/shift/{id}/ – (+) manually edited
export const getSchedulerShift = async (id: number): Promise<SchedulerShift> => {
  return (await api.get(`/sched/shift/${id}/`)).data;
};

// PUT /sched/shift/{id}/ – (+) manually edited
export const putSchedulerShift = async (id: number, params: SchedulerShift): Promise<SchedulerShiftItem> => {
  return (await api.put(`/sched/shift/${id}/`, params)).data;
};

// POST /sched/shift/copy_from_date/ – (+) manually edited
export const getSchedulerShiftFromDate = async (dateFrom: string, dateTo: string): Promise<SchedulerShiftItem[]> => {
  return (await api.post(`/sched/shift/copy_from_date/?from_date=${dateFrom}&to_date=${dateTo}`)).data;
};

export const getSchedulerShiftFromLast = async (dateTo: string): Promise<SchedulerShiftItem[]> => {
  return (await api.post(`/sched/shift/copy_from_last/?to_date=${dateTo}`)).data;
};

export const getSchedulerGenerateShift = async (dateTo: string): Promise<SchedulerShiftItem[]> => {
  return (await api.post(`/sched/shift/generate_shift/?to_date=${dateTo}`)).data;
};

export const getTravelTimes = async (board_id: number): Promise<SchedulerTravelTimeForBoardResponse[]> => {
  return (await api.get(`/sched/board/${board_id}/travel_times/`)).data;
};

/**
 *
 * // Scheduler //
 *
 */

/**
 *
 * ** Drafts: **
 *
 */

// GET /job/draft/ – Returns job drafts for user's customer_code
export const getDrafts = async (params: DraftsRequestData): Promise<DraftsResponseData> => {
  const queryString = generateQueryString(params);
  return (await api.get(`job/draft/${queryString}`)).data;
};

// POST /job/draft/ – Creates draft
// res.status === 201
export const createDraft = async (data: JobDraftParams): Promise<AxiosResponse> => {
  return await api.post('job/draft/', data);
};

// GET /job/draft/{id}/ – Returns job draft with additional info
export const getDraft = async (id: number): Promise<JobDraftsListItem> => {
  return (await api.get(`job/draft/${id}/`)).data;
};

// PUT /job/draft/{id}/ – Updates job draft
// res.status === 200
export const editDraft = async (id: number, data: JobDraftParams): Promise<AxiosResponse> => {
  return await api.put(`job/draft/${id}/`, data);
};

// DELETE /job/draft/{id}/ – Deletes job draft
// res.status === 204
export const deleteDrafts = async (id: number): Promise<AxiosResponse> => {
  return await api.delete(`job/draft/${id}/`);
};
/**
 *
 * // Drafts //
 *
 */

/**
 *
 * ** Zammad: **
 *
 */

// GET /zammad/quotes – Gets quote tickets for current user from Zammad
export const getZammadQuotes = async (): Promise<AxiosResponse> => {
  return api.get('/zammad/quotes/');
};

export const getZammadEmailLookup = (name: string, signal): Promise<AxiosResponse> => {
  return api.get(`/zammad/lookup/email?email_guess=${name}`, { signal });
};

export const getZammadChannelsEmail = async (): Promise<string[]> => {
  return (await api.get('/zammad/channels-emails/')).data;
};

/**
 *
 * // Zammad //
 *
 */

/**
 *
 * ** News: **
 *
 */

// GET /news/ – List news
export const getNews = async (): Promise<AxiosResponse> => {
  return await api.get('news/?exclude_type=newsletter');
};
// GET /news/ – List news type of newsletter
export const getNewsEntry = async (id: number | string): Promise<AxiosResponse> => {
  return await api.get(`news/${id}`);
};
/**
 *
 * // News //
 *
 */

export const getSchedulerStops = async (board: number): Promise<SchedulerUnscheduledStop[]> => {
  return (await api.get(`sched/board/${board}/stops_for_board/`)).data;
};

export const getSchedulerNewStops = async (board: number): Promise<SchedulerUnscheduledStop[]> => {
  return (await api.get(`sched/board/${board}/new_stops_for_board/`)).data;
};

export const getSchedulerTravelTime = async (
  suburb1: number,
  suburb2: number,
): Promise<SchedulerTravelTimeResponse> => {
  const travelTimeResponse = await api.get(`sched/travel_time/?suburb1=${suburb1}&suburb2=${suburb2}`);
  return travelTimeResponse.data;
};

export const getSchedulerScheduledStops = async (id: number): Promise<SchedulerScheduledStopsList[]> => {
  return (await api.get(`sched/board/${id}/list_schedules/`)).data;
};

export const getSchedulerBoard = async (id: number): Promise<SchedulerBoardCreated> => {
  return (await api.get(`/sched/board/${id}/`)).data;
};

export const getSchedulerBoards = async (params: SchedulerBoardsPayload): Promise<SchedulerBoardCreated[]> => {
  const payload = Object.keys(params)
    .map(key => key + '=' + params[key])
    .join('&');
  return (await api.get(`/sched/board/?${payload}`)).data;
};

export const lockSchedulerBoard = async (id: number): Promise<SchedulerBoardCreated> => {
  return (await api.post(`/sched/board/${id}/lock/`)).data;
};

export const grabSchedulerBoard = async (id: number): Promise<SchedulerBoardCreated> => {
  return (await api.post(`/sched/board/${id}/grab/`)).data;
};

export const unlockSchedulerBoard = async (id: number): Promise<SchedulerBoardCreated> => {
  return (await api.post(`/sched/board/${id}/unlock/`)).data;
};

export const createSchedulerBoard = async (params: SchedulerBoardsPayload): Promise<SchedulerBoardCreated> => {
  return (await api.post('/sched/board/', params)).data;
};

export const saveSchedulerBoardStops = async (
  id: number,
  params: SchedulerScheduledStopsList[],
): Promise<SchedulerSaveBoardResponse> => {
  return (await api.post(`sched/board/${id}/save_schedules/`, params)).data;
};

// export const commitSchedulerBoard = async (id: number): Promise<AxiosResponse> => {
//   return (await api.post(`sched/board/${id}/commit/`)).data;
// };

export const getSchedulerBoardLogs = async (id: number): Promise<SchedulerBoardLogsData[]> => {
  return (await api.get(`/sched/board_log/?board=${id}`)).data;
};

// Allocate / deallocate stops, trucks, boards
export const allocateJobFromScheduler = async (
  schedule_id: string,
  board_id: number,
  requestConfig?: AxiosRequestConfig,
): AxiosPromise<SchedulerAllocationsResponse> => {
  return await api.post(`sched/alloc/job/allocate/?schedule=${schedule_id}&board=${board_id}`, {}, requestConfig);
};

export const deallocateJobFromScheduler = async (
  schedule_id: string,
  board_id: number,
  requestConfig?: AxiosRequestConfig,
): AxiosPromise<SchedulerAllocationsResponse> => {
  return await api.post(`sched/alloc/job/deallocate/?schedule=${schedule_id}&board=${board_id}`, {}, requestConfig);
};

export const allocateTruckFromScheduler = async (
  truck: string,
  board_id: number,
  requestConfig?: AxiosRequestConfig,
): AxiosPromise<SchedulerAllocationsResponse> => {
  return await api.post(`sched/alloc/truck/allocate/?truck=${truck}&board=${board_id}`, {}, requestConfig);
};

export const deallocateTruckFromScheduler = async (
  truck: string,
  board_id: number,
  requestConfig?: AxiosRequestConfig,
): AxiosPromise<SchedulerAllocationsResponse> => {
  return await api.post(`sched/alloc/truck/deallocate/?truck=${truck}&board=${board_id}`, {}, requestConfig);
};

export const allocateBoardFromScheduler = async (
  board_id: number,
  requestConfig?: AxiosRequestConfig,
): AxiosPromise<SchedulerAllocationsResponse> => {
  return await api.post(`sched/alloc/board/allocate/?board=${board_id}`, {}, requestConfig);
};

export const deallocateBoardFromScheduler = async (
  board_id: number,
  requestConfig?: AxiosRequestConfig,
): AxiosPromise<SchedulerAllocationsResponse> => {
  return await api.post(`sched/alloc/board/deallocate/?board=${board_id}`, {}, requestConfig);
};

export const saveSchedulerBoard = async (
  board_id: number,
  params: SchedulerScheduledStopsList[],
  requestConfig?: AxiosRequestConfig,
): AxiosPromise<SchedulerSaveBoardResponse[]> => {
  return await api.post(`sched/board/${board_id}/save_schedules/`, params, requestConfig);
};

export const commitSchedulerBoard = async (
  id: number,
  requestConfig?: AxiosRequestConfig,
): AxiosPromise<AxiosResponse> => {
  return await api.post(`sched/board/${id}/commit/`, {}, requestConfig);
};

export const preallocateSchedulerBoard = async (
  id: number,
  requestConfig?: AxiosRequestConfig,
): AxiosPromise<AxiosResponse> => {
  return await api.post(`sched/board/${id}/preallocate/`, {}, requestConfig);
};

export const getJobTicket = async (job_id: number): Promise<JobTicketResponce> => {
  return (await api.get(`zammad/tickets/?ticket_id=${job_id}`)).data;
};

export const getJobTicketsByJobIdShort = async (job_id: number, job_date: string): Promise<JobTicketResponce[]> => {
  return (await api.get(`job/tickets/search_by_job/?job_id=${job_id}&job_date=${job_date}`)).data;
};

export const createJobTicket = async (data: JobTicket): Promise<JobTicketResponce> => {
  return (await api.post('zammad/tickets/', data)).data;
};

export const editJobTicket = async (data: JobTicketResponce): Promise<JobTicketResponce> => {
  return (await api.put('zammad/tickets/', data)).data;
};

export const getJobArticles = async (ticket_id: number): Promise<JobTicketAtricle[]> => {
  return (await api.get(`zammad/articles/?ticket_id=${ticket_id}`)).data;
};

export const createJobArticle = async (article: JobArticleData): Promise<JobTicketAtricle> => {
  return (await api.post('zammad/articles/', article)).data;
};

export const editJobArticle = async (article: JobArticleData): Promise<JobTicketAtricle> => {
  return (await api.put('zammad/articles/', article)).data;
};

// Postcode Search Lookup
export const lookupPostcode = async (postcode: number): Promise<PostcodeDataResponce> => {
  return (await api.get(`/lookup/postcode/?postcode=${postcode}`)).data;
};

// Search by postcode
export const searchJobsByPostcode = async (params: LookupPostcodeData): Promise<IGetJobs> => {
  const queryString = generateQueryString(params);
  return (await api.get(`dashboard/search_by_postcode/${queryString}`)).data;
};

// Notifications
export const getNotifications = async (
  unread_filter: boolean,
  category: string | null,
  page?: number,
): Promise<ModuleNotifications> => {
  let queryString = '/notifications/';
  unread_filter ? (queryString += 'unread_list/') : (queryString += 'all_list/');
  queryString += `?page=${page}`;
  if (category) {
    queryString += `&category=${category}`;
  }
  return (await api.get(queryString)).data;
};

export const getNotificationsAllCount = async (): Promise<number> => {
  return (await api.get('/notifications/all_count/')).data;
};

export const getNotificationsUnreadCount = async (): Promise<number> => {
  return (await api.get('/notifications/unread_count/')).data;
};

export const getTicketsNotificationsUnreadCount = async (): Promise<number> => {
  return (await api.get('/ticket/dashboard/unread_count')).data;
};

export const getNotificationsAllList = async (): Promise<ModuleNotifications[]> => {
  return (await api.get('/notifications/all_list/')).data;
};

export const getNotificationsUnreadList = async (): Promise<AxiosResponse> => {
  return await api.get('/notifications/unread_list/');
};

export const setNotificationAsUnreadRequest = async (id: number): Promise<AxiosResponse> => {
  return await api.post(`/notifications/notifications/${id}/mark_as_unread/`);
};

export const setNotificationsAllAsReadRequest = async (): Promise<AxiosResponse> => {
  return await api.post('/notifications/notifications/mark_all_as_read/');
};

export const setNotificationAsReadRequest = async (id: number): Promise<AxiosResponse> => {
  return await api.post(`/notifications/notifications/${id}/mark_as_read/`);
};

export const deleteNotification = async (id: number): Promise<AxiosResponse> => {
  return await api.delete(`notifications/notifications/${id}/`);
};

export const setTicketNotificationAsReadRequest = async (id: number): Promise<AxiosResponse> => {
  return await api.post(`/ticket/notifications/mark_all_as_read/?ticket_id=${id}`);
};

export const getNotificationsAllSorted = async (): Promise<ModuleNotificationsItemsData> => {
  return (await api.get('/notifications/all_sorted/')).data;
};

export const getUnreadTicketsDahboard = async (showAll, params): Promise<JobTicketDashboardResponse> => {
  const queryString = generateQueryString(params);
  return (await api.get(`/ticket/dashboard/${showAll ? 'all' : 'unread'}/${queryString}`)).data;
};

// Static lists
export const getDriversList = async (): Promise<JobComplianceDriver[]> => {
  return (await api.get('/catalog/drivers/'))?.data;
};

export const getServiceCodesList = async (): Promise<JobComplianceServiceLookupData[]> => {
  return (await api.get('/catalog/service-codes/'))?.data;
};

export const getAttributesList = async (): Promise<JobComplianceAttribute[]> => {
  return (await api.get('/catalog/attributes/'))?.data;
};

export const getProductTypesList = async (): Promise<JobComplianceProductLookupData[]> => {
  return (await api.get('/catalog/goods-types/'))?.data;
};

export const getTechniciansList = async (requestConfig?: AxiosRequestConfig): AxiosPromise<TechnicianCatalogItem[]> => {
  return await api.get('/catalog/technicians/', requestConfig);
};

export const createTechnicianItem = async (
  data,
  requestConfig?: AxiosRequestConfig,
): AxiosPromise<TechnicianCatalogItem> => {
  return await api.post('/catalog/technicians/', data, requestConfig);
};

export const updateTechnicianItem = async (
  id: number,
  data,
  requestConfig?: AxiosRequestConfig,
): AxiosPromise<TechnicianCatalogItem> => {
  return await api.patch(`/catalog/technicians/${id}/`, data, requestConfig);
};

export const getTechniciansJobs = async (
  dates: string,
  page: number,
  page_size: number,
  requestConfig?: AxiosRequestConfig,
): AxiosPromise<TechniciansJobsResponseData> => {
  return await api.get(`/tech/alloc/?dates=${dates}&page=${page}&page_size=${page_size}`, requestConfig);
};

export const updateTechniciansJobs = async (data, requestConfig?: AxiosRequestConfig): AxiosPromise<AxiosResponse> => {
  return await api.put('/tech/alloc/', data, requestConfig);
};

// Job compliance
//
// Preallocate / Allocate / Deallocate
export const setJobAllocation = async (params: JobComplianceDriverAllocateParams): Promise<AxiosResponse> => {
  return api.post('/job/allocation/', params);
};

export const setJobEditAllocation = async (
  job_id: number,
  job_date: string,
  params: JobComplianceDriverAllocateParams,
): Promise<AxiosResponse> => {
  return api.post(`/jobedit/allocation/?job_id=${job_id}&job_date=${job_date}`, params);
};

// Cancel
export const setJobEditAsCancel = (
  job_id,
  job_date,
  params: JobEditCancelParams,
  requestConfig?: AxiosRequestConfig,
): Promise<AxiosResponse> => {
  return api.post(`/jobedit/cancel/?job_id=${job_id}&job_date=${job_date}`, params, requestConfig);
};

// Futile
export const setJobEditAsFutile = (
  job_id,
  job_date,
  params: { [key: string]: string },
  requestConfig?: AxiosRequestConfig,
): Promise<AxiosResponse> => {
  return api.post(`/jobedit/futile/?job_id=${job_id}&job_date=${job_date}`, params, requestConfig);
};
export const setJobAsCancel = (params: JobCancelParams, requestConfig?: AxiosRequestConfig): Promise<AxiosResponse> => {
  return api.post('/job/cancel/', params, requestConfig);
};

export const changeJobDate = async (
  params: JobChangeDateParams,
  date: string,
  requestConfig?: AxiosRequestConfig,
): Promise<AxiosResponse> => {
  return api.post(
    `/job/change_date/${generateQueryString(params)}`,
    {
      job_date: date,
    },
    requestConfig,
  );
};

export const setJobAsFutile = (params: JobFutileParams, requestConfig?: AxiosRequestConfig): Promise<AxiosResponse> => {
  return api.post('/job/futile/', params, requestConfig);
};

// Add Stairs Service
export const setJobAddStairs = async (job_id, job_date, stop_number): Promise<AxiosResponse> => {
  return api.post(`/jobedit/add_stairs/?job_id=${job_id}&job_date=${job_date}&stop_number=${stop_number}`, [{ id: 0 }]);
};

// Change Customer Code
export const setJobCustomerCode = async (
  job_id: number,
  job_date: string,
  params: JobEditChangeCustomerParams,
  requestConfig?: AxiosRequestConfig,
): Promise<AxiosResponse> => {
  return api.put(`/jobedit/customer/?job_id=${job_id}&job_date=${job_date}`, params, requestConfig);
};

// GET /odoo/customer/quants/ - enpoint for fetchData in Quant List Page
export const getQuantListData = async (params: TQuantDataParams): Promise<QuantListData> => {
  return (await api.get(`/odoo/customer/quants/${generateQueryString(params)}`)).data;
};

export const getJobWithoutLock = async (job_id: number, job_date: string): Promise<JobComplianceData> => {
  return (await api.get(`/jobedit/job/?job_id=${job_id}&job_date=${job_date}`)).data;
};

export const setJobLock = async (
  job_id: number,
  job_date: string,
  requestConfig?: AxiosRequestConfig,
): AxiosPromise<JobLockResponse> => {
  return await api.post(`/jobedit/lock/?job_id=${job_id}&job_date=${job_date}`, {}, requestConfig);
};

export const setJobUnlock = async (job_id: number, job_date: string): AxiosPromise<JobLockResponse> => {
  return await api.post(`/jobedit/unlock/?job_id=${job_id}&job_date=${job_date}`);
};

export const setJobCustomerDetails = async (
  job_id: number,
  job_date: string,
  data,
  requestConfig?: AxiosRequestConfig,
): Promise<{ [key: string]: string }> => {
  return await api.put(`/jobedit/job-details/?job_id=${job_id}&job_date=${job_date}`, data, requestConfig);
};

export const createJobStopData = async (
  job_id: number,
  job_date: string,
  stop_number: number,
  data,
  requestConfig?: AxiosRequestConfig,
): Promise<AxiosResponse> => {
  return await api.post(
    `/jobedit/stop/?job_id=${job_id}&job_date=${job_date}&stop_number=${stop_number}`,
    data,
    requestConfig,
  );
};

export const setJobStopData = async (
  job_id: number,
  job_date: string,
  stop_number: number,
  data,
  requestConfig?: AxiosRequestConfig,
): Promise<{ [key: string]: string }> => {
  return await api.put(
    `/jobedit/stop/?job_id=${job_id}&job_date=${job_date}&stop_number=${stop_number}`,
    data,
    requestConfig,
  );
};

export const removeJobStop3 = async (
  job_id: number,
  job_date: string,
  stop_number: number,
  requestConfig?: AxiosRequestConfig,
): Promise<AxiosStatusResponse> => {
  return (
    await api.delete(`/jobedit/stop/?job_id=${job_id}&job_date=${job_date}&stop_number=${stop_number}`, requestConfig)
  ).data;
};

export const setJobStopProducts = async (
  job_id: number,
  job_date: string,
  stop_number: number,
  data,
  requestConfig?: AxiosRequestConfig,
): Promise<JobComplianceStopProductData> => {
  return await api.put(
    `/jobedit/products/?job_id=${job_id}&job_date=${job_date}&stop_number=${stop_number}`,
    data,
    requestConfig,
  );
};

export const setJobStopServices = async (
  job_id: number,
  job_date: string,
  stop_number: number,
  data,
  requestConfig?: AxiosRequestConfig,
): Promise<JobComplianceStopService[]> => {
  return await api.put(
    `/jobedit/services/?job_id=${job_id}&job_date=${job_date}&stop_number=${stop_number}`,
    data,
    requestConfig,
  );
};

export const setJobOnHold = async (job_id: number, job_date: string): Promise<JobComplianceHoldResponse> => {
  return (await api.post(`job/set_on_hold/?job_id=${job_id}&job_date=${job_date}`)).data;
};

export const unsetJobOnHold = async (job_id: number, job_date: string): Promise<JobComplianceHoldResponse> => {
  return (await api.post(`job/unset_on_hold/?job_id=${job_id}&job_date=${job_date}`)).data;
};

export const getDriverLocations = async (params: DriverLocationsParams): Promise<DriverLocations[]> => {
  const queryString = generateQueryString(params);
  return (await api.get(`/location/path/${queryString}`)).data;
};

export const getDriverOnDay = async (date: string): Promise<number[]> => {
  return (await api.get(`/location/drivers_on_day/?date=${date}`)).data;
};

export const getJobHistory = async (id: number, date: string): Promise<JobHistory[]> => {
  return (await api.get(`/job/history/?job_id=${id}&job_date=${date}`)).data;
};

export const getNextDayStops = async (params: NextDayStopsParams): Promise<Package[]> => {
  const queryString = generateQueryString(params);
  return (await api.get(`/location/next_day_stops/${queryString}`)).data;
};

export const getPriceLeg = async (
  id: number,
  date: string,
  leg: number,
  requestConfig?: AxiosRequestConfig,
): AxiosPromise<JobComplianceStopService[]> => {
  return await api.get(`/job/price_leg/?job_id=${id}&job_date=${date}&leg=${leg}`, requestConfig);
};

export const getDriversRetransmit = async (
  date: string,
  requestConfig?: AxiosRequestConfig,
): AxiosPromise<DriverRetransmit[]> => {
  return await api.get(`driver/retransmit/?date=${date}`, requestConfig);
};

export const postDriversRetransmit = async (
  date: string,
  driver: number,
  requestConfig?: AxiosRequestConfig,
): AxiosPromise => {
  return await api.post(`driver/retransmit/?date=${date}&driver_id=${driver}`, {}, requestConfig);
};

/**
 *
 * ** Quote Calculator: **
 *
 */

// GET /api/quote/options/ – Available options for the customer's rate card
export const getQuoteOptions = async (customerCode?: string): Promise<QuoteOptions> => {
  const params = customerCode ? `?customer_code=${customerCode}` : '';
  return (await api.get(`/quote/options/${params}`)).data;
};

// POST /api/quote/calculate/ – Stateless price calculation.
// Single call covers the whole quote: job_type + surcharges live at the top level,
// and per-item details (machine_size, quantity, weight, action, tech_level) are in items[].
export const calculateQuote = async (data: QuoteCalculateRequest): Promise<QuoteCalculateResponse> => {
  return (await api.post('/quote/calculate/', data)).data;
};

// POST /api/quote/ – Save a quote. Body is the same shape as calculate, plus reference/notes/valid_until.
// The backend builds its own source_data snapshot — no free-form payload from the FE.
export const saveQuote = async (data: QuoteSaveRequest): Promise<QuoteDetail> => {
  return (await api.post('/quote/', data)).data;
};

// GET /api/quote/ – List saved quotes (paginated)
export const getQuotes = async (page: number, pageSize: number, status?: string): Promise<QuoteListResponse> => {
  const params: string[] = [`page=${page}`, `page_size=${pageSize}`];
  if (status) {
    params.push(`status=${status}`);
  }
  return (await api.get(`/quote/?${params.join('&')}`)).data;
};

// GET /api/quote/{id}/ – Full quote detail
export const getQuoteDetail = async (id: number): Promise<QuoteDetail> => {
  return (await api.get(`/quote/${id}/`)).data;
};

// PATCH /api/quote/{id}/ – Update status, reference, or notes
export const updateQuote = async (id: number, data: QuoteUpdateRequest): Promise<QuoteDetail> => {
  return (await api.patch(`/quote/${id}/`, data)).data;
};

// POST /api/quote/{id}/link_job/ – Link quote to an existing job
export const linkQuoteToJob = async (id: number, jobId: number): Promise<AxiosResponse> => {
  return api.post(`/quote/${id}/link_job/`, { job_id: jobId });
};

// POST /api/quote/{id}/email/ – Render PDF, send email, set status=sent
export const emailQuote = async (id: number, emailTo: string): Promise<QuoteDetail> => {
  return (await api.post(`/quote/${id}/email/`, { email_to: emailTo })).data;
};

// GET /api/quote/{id}/pdf/ – Download quote PDF
export const downloadQuotePdf = async (id: number): Promise<Blob> => {
  const response = await api.get(`/quote/${id}/pdf/`, { responseType: 'blob' });
  return response.data;
};

// GET /api/quote/{id}/files/ – List files attached to a quote
export const getQuoteFiles = async (id: number): Promise<QuoteFile[]> => {
  return (await api.get(`/quote/${id}/files/`)).data;
};

// POST /api/quote/{id}/files/ – Upload a file to a quote
export const uploadQuoteFile = async (id: number, file: File): Promise<QuoteFile> => {
  const formData = new FormData();
  formData.append('file', file);
  return (
    await api.post(`/quote/${id}/files/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  ).data;
};

// DELETE /api/quote/{id}/files/{fileUuid}/ – Delete a quote file
export const deleteQuoteFile = async (id: number, fileUuid: string): Promise<void> => {
  await api.delete(`/quote/${id}/files/${fileUuid}/`);
};

/**
 *
 * // Quote Calculator //
 *
 */

/**
 *
 * // Permanent Jobs //
 *
 */

// GET /api/permanent/ – List permanent job templates (paginated)
export const getPermanentTemplates = async (
  page: number,
  pageSize: number,
  customerCode?: string,
): Promise<PermanentListResponse> => {
  const params: string[] = [`page=${encodeURIComponent(page)}`, `page_size=${encodeURIComponent(pageSize)}`];
  if (customerCode) {
    params.push(`customer_code=${encodeURIComponent(customerCode)}`);
  }
  return (await api.get(`/permanent/?${params.join('&')}`)).data;
};

// GET /api/permanent/{id}/ – Single permanent job template detail
export const getPermanentTemplate = async (id: number): Promise<PermanentTemplate> => {
  return (await api.get(`/permanent/${id}/`)).data;
};

// POST /api/permanent/?dry_run=false – Create permanent job template
// API expects an array of payloads, same as bookJobAdvanced
export const createPermanentTemplate = async (data: PermanentCreatePayload): Promise<PermanentBookingResponse> => {
  return (await api.post('/permanent/?dry_run=false', [data])).data;
};
